import {
  Button,
  FlexBox,
  GroupControlListItem,
  Loader,
  Modal,
  space,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { Dispatch, FC, SetStateAction } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { ReactComponent as SuccessIcon } from "../../public/material-icons/check-circle.svg";
import type { LightStatus } from "../api/laboperator.types";
import { setControlValue } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import {
  fetchControlFunctions,
  findGroupControl,
  resolveOperationLabel,
} from "../hooks/use-control";
import { useDeviceImage } from "../hooks/use-device-image";
import type { Device, OperationMode } from "../view-model";

// Prefer the OPC-UA DeviceTypeImage, fall back to the static device.image.
const GroupControlListItemWithDeviceImage: FC<
  { device: Device } & Omit<
    React.ComponentProps<typeof GroupControlListItem>,
    "imageUrl"
  >
> = ({ device, ...props }) => {
  const imageUrl = useDeviceImage(device.deviceTypeImage, device.image);
  return <GroupControlListItem {...props} imageUrl={imageUrl} />;
};

type GroupControlModalProps = {
  devices: Device[];
  isOpenModal: boolean;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  type: "operation_status" | "light";
  mode: OperationMode | LightStatus;
};

const StyledGroupControlContainer = styledComponents.div`
  height: 560px;
  margin-top: ${space(3)};
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  > * {
    border-top: 1px solid ${theme.colorPalette.grey200};
  }
`;
const StyledButtonContainer = styledComponents.div`
  display: flex;
  gap: ${space(4)};
  justify-content: flex-end;
  border-top: 1px solid ${theme.colorPalette.grey200};
  padding: ${space(4)} 0;
  padding-bottom: 0;
`;

export const GroupControlModal = ({
  devices,
  isOpenModal,
  mode,
  setIsOpenModal,
  type,
}: GroupControlModalProps) => {
  const defaultSelected = devices
    .filter((device) => {
      if (
        device.state?.operation === "off" ||
        device.state?.operation === "high"
      ) {
        return false;
      }
      return true;
    })
    .map((device) => device.id);
  const { t } = useTranslation();
  const token = useAccessToken();
  const [loading, setLoading] = useState(false);
  const [controlStep, setControlStep] = useState<1 | 2>(1);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedDevices, setSelectedDevices] =
    useState<string[]>(defaultSelected);

  const onHandleCloseModal = () => {
    setControlStep(1);
    setIsOpenModal(false);
    setSelectedDevices(defaultSelected);
    setSubmitError(null);
  };

  const onSelectDevice = (device: Device, checked: boolean) => {
    if (checked) {
      setSelectedDevices([...selectedDevices, device.id]);
    } else {
      setSelectedDevices(
        selectedDevices.filter((selectedDevice) => selectedDevice !== device.id)
      );
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    setSubmitError(null);

    const failed: string[] = [];
    await Promise.all(
      devices
        .filter((device) => selectedDevices.includes(device.id))
        .map(async (device) => {
          try {
            const controls = await fetchControlFunctions(
              device.id,
              token ?? undefined
            );
            const control = findGroupControl(controls, type);
            if (!control) throw new Error("control function not found");
            // Discrete writes take the device's enum label; the light buttons
            // send 1/0, which double as the EnumStrings index (0=off, 1=on).
            // Devices without EnumStrings (plain Boolean) get the boolean.
            const value =
              type === "operation_status"
                ? resolveOperationLabel(control, String(mode))
                : control.enumStrings?.[mode as number] ?? mode === 1;
            await setControlValue(
              control.functionPath,
              value,
              token ?? undefined
            );
          } catch {
            failed.push(device.name || device.id);
          }
        })
    );
    setLoading(false);
    if (failed.length > 0) {
      setSubmitError(
        `${t("control.error", "Steuerung fehlgeschlagen")}: ${failed.join(
          ", "
        )}`
      );
      return;
    }
    setControlStep(2);
    // The dashboard's useWorkplaceGroupState listens on the LADS WebSocket and
    // re-reads when the device echoes the written TargetValue — no manual
    // revalidation from here needed.
  };

  return (
    <Modal
      onClose={() => onHandleCloseModal()}
      open={isOpenModal}
      width="700px"
    >
      <Typography size="xl">
        {t(`channel.${type}`)} &apos;{t(`detail.mode.${mode}`)}&apos; {t(`set`)}
      </Typography>
      {controlStep === 1 ? (
        <>
          <StyledGroupControlContainer>
            {devices.map((device) => (
              <GroupControlListItemWithDeviceImage
                checked={selectedDevices.includes(device.id)}
                device={device}
                infoSeverity={
                  device.state?.operation === "high" ? "warning" : "info"
                }
                infoText={
                  device.state?.operation === "high" ||
                  device.state?.operation === "off"
                    ? (t(
                        `device.operation.info.${device.state?.operation}`
                      ) as string)
                    : undefined
                }
                key={device.id}
                location={device.location.position || ""}
                modelText={device.name || t("data.missing.name")}
                onChange={(checked) => onSelectDevice(device, checked)}
              />
            ))}
          </StyledGroupControlContainer>
          <StyledButtonContainer>
            {submitError && (
              <Typography color="orange700" size="xs">
                ⚠ {submitError}
              </Typography>
            )}
            {loading && <Loader />}
            <Button
              disabled={loading}
              onClick={() => onHandleCloseModal()}
              variant="secondary"
            >
              {t("button.back")}
            </Button>
            <Button
              disabled={loading}
              onClick={() => onSubmit()}
              variant="primary"
            >
              {t("button.accept")}
            </Button>
          </StyledButtonContainer>
        </>
      ) : (
        <>
          <FlexBox alignItems="center" height="560px" justifyContent="center">
            <FlexBox alignItems="center" gap={space(2)}>
              <SuccessIcon
                color={theme.colorPalette.green500}
                fontSize="30px"
              />
              <Typography size="lg">{t("control.success")}</Typography>
            </FlexBox>
          </FlexBox>
          <StyledButtonContainer>
            <Button onClick={() => onHandleCloseModal()} variant="secondary">
              {t("report.close")}
            </Button>
          </StyledButtonContainer>
        </>
      )}
    </Modal>
  );
};
